import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, fmtMoney, type PaymentMethod, type ReturnTypeT } from "../api";
import {
  aggregateLines,
  buildReturnedIndex,
  fetchAllReturns,
  type ReturnableLine,
} from "../lib/returns-index";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { Textarea } from "./ui/textarea";
import { QtyStepper } from "./qty-stepper";
import { PaymentMethodSelect } from "./payment-method-select";

type Row = {
  product_id: number;
  nomi: string;
  originalQty: number;
  returnedQty: number;
  maxQty: number;
  avgPrice: number;
  qty: string;
};

/**
 * Qaytarish (vozvrat) dialogi — xarid (yetkazib beruvchiga) yoki sotuv
 * (xaridordan) hujjati bo'yicha qisman qaytarish. Ochilganda hujjatning oldingi
 * qaytarishlari yuklanadi va har tovar uchun qoldiq limit hisoblanadi; miqdor
 * shu limitdan oshmaydi. Yuborishdan oldin bir bosqichli tasdiq so'raladi.
 * Aniq summa (vaznlangan o'rtacha narx) serverda hisoblanadi — bu yerdagisi
 * taxmin sifatida ko'rsatiladi. Xatoda dialog ochiq qoladi.
 */
export function ReturnDialog({
  open,
  onOpenChange,
  kind,
  docId,
  partyLabel,
  lines,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: ReturnTypeT;
  docId: number;
  partyLabel?: string;
  lines: ReturnableLine[];
  onDone: () => void;
}) {
  const { t } = useTranslation(["ombor", "common"]);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [reason, setReason] = useState("");
  // Pul QANDAY qaytarilgani — bu yerda pul teskari yo'nalishda harakat qiladi.
  // Hujjat AYNAN HOZIR yozilyapti, shuning uchun sukut bo'yicha naqd; bo'sh
  // ("yozilmagan") tanlov ro'yxatda qoladi va bir bosish bilan tanlanadi.
  const [usul, setUsul] = useState<PaymentMethod | "">("naqd");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Ota komponent dialogni shartli mount qiladi — mount == ochilish.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const docs = await fetchAllReturns(kind, docId);
        if (!alive) return;
        const perDoc = buildReturnedIndex(docs).get(docId);
        setRows(
          aggregateLines(lines).map((a) => {
            const returned = perDoc?.get(a.product_id) ?? 0;
            const max = Math.max(0, Math.round((a.qty - returned) * 1000) / 1000);
            return {
              product_id: a.product_id,
              nomi: a.nomi,
              originalQty: a.qty,
              returnedQty: returned,
              maxQty: max,
              avgPrice: a.avgPrice,
              qty: "0",
            };
          }),
        );
      } catch (e) {
        if (!alive) return;
        setErr((e as { message?: string }).message || t("common:common.error"));
        setRows([]);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = (rows ?? []).filter((r) => Number(r.qty) > 0);
  const estTotal = selected.reduce(
    (s, r) => s + Number(r.qty) * r.avgPrice,
    0,
  );

  async function doSubmit() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.createReturn({
        return_type: kind,
        original_document_id: docId,
        reason: reason.trim() || undefined,
        tolov_usuli: usul || null,
        items: selected.map((r) => ({
          product_id: r.product_id,
          quantity: r.qty,
        })),
      });
      toast.success(t("shared.return.saved"));
      (res.ogohlantirishlar || []).forEach((w) => toast.warning(w));
      onDone();
      onOpenChange(false);
    } catch (e) {
      setErr((e as { message?: string }).message || t("common:common.error"));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      contentClassName="max-w-xl"
    >
      <div className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t(
              kind === "purchase_return"
                ? "shared.return.purchaseTitle"
                : "shared.return.saleTitle",
            )}
          </h2>
          {partyLabel && (
            <p className="mt-1.5 text-sm text-muted-foreground">{partyLabel}</p>
          )}
        </div>

        {rows === null ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : (
          <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
            {rows.map((r, i) => (
              <div
                key={r.product_id}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{r.nomi}</div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    {t("shared.return.returnedOf", {
                      returned: r.returnedQty,
                      original: r.originalQty,
                    })}
                    {" · "}
                    {fmtMoney(r.avgPrice)}
                  </div>
                </div>
                {r.maxQty > 0 ? (
                  <QtyStepper
                    value={r.qty}
                    onChange={(v) =>
                      setRows((prev) =>
                        prev
                          ? prev.map((x, j) => (j === i ? { ...x, qty: v } : x))
                          : prev,
                      )
                    }
                    max={r.maxQty}
                    disabled={busy}
                  />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t("shared.return.noneLeft")}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("shared.return.reasonLabel")}
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            rows={2}
            placeholder={t("shared.return.reasonPlaceholder")}
          />
        </div>

        <PaymentMethodSelect
          id="ret-usul"
          value={usul}
          onChange={setUsul}
          disabled={busy}
          className="space-y-1"
        />

        <div className="space-y-1 rounded-md border bg-muted/30 px-3 py-2.5">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("shared.return.estTotal")}
            </span>
            <span className="font-medium tabular-nums">{fmtMoney(estTotal)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("shared.return.estNote")}
          </p>
        </div>

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        {confirming ? (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {t("shared.return.confirmQuestion", { count: selected.length })}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="flex-none"
              >
                {t("shared.return.back")}
              </Button>
              <Button
                type="button"
                onClick={doSubmit}
                disabled={busy}
                className="flex-1"
              >
                {busy
                  ? t("shared.return.submitting")
                  : t("shared.return.confirmYes")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={busy}
              className="flex-none"
            >
              {t("sotuvlar.cancel")}
            </Button>
            <Button
              type="button"
              onClick={() => setConfirming(true)}
              disabled={busy || rows === null || selected.length === 0}
              className="flex-1"
            >
              {t("shared.return.submit")}
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
